import { ShoppingBag } from 'lucide-react';
import { SectionCardHeader } from '../../app/section-card-header.js';
import { Button, formatCurrency } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { salesApi, getApiError } from '../../lib/api.js';
import { RequestAlert } from '../../app/data-view.js';
import { SalePaymentsDialog } from './sale-payments-dialog.js';
import { PaymentStatePill, PaymentSummary } from './sale-payment-summary.js';

/// A venda do atendimento (ADR 0059): gerada e aumentada pela tela da Nova venda, que já abre com
/// o cliente e o serviço dele. Na sessão de pacote, a venda leva só o que o cliente comprar a mais.
export function AppointmentSale({
  appointmentId,
  prepaid,
  cancelled,
  onOpenSale,
  canCreate,
  canManagePayments,
  financeContracted,
  today,
}: {
  appointmentId: string;
  prepaid: boolean;
  cancelled: boolean;
  onOpenSale: () => void;
  canCreate: boolean;
  canManagePayments: boolean;
  financeContracted: boolean;
  today: string;
}) {
  const queryClient = useQueryClient();
  const sale = useQuery({
    queryKey: ['company', 'sales', 'appointment', appointmentId],
    queryFn: () => salesApi.appointmentSale(appointmentId),
    retry: false,
  });
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  if (cancelled && sale.isSuccess && !sale.data) return null;
  const found = sale.data;
  return (
    <div className="rounded-2xl border border-surface-border bg-surface-card shadow-soft">
      <SectionCardHeader title="Venda do atendimento" icon={ShoppingBag} />
      <div className="grid gap-3 p-4">
        {sale.isLoading ? (
          <p role="status" className="text-sm">
            Consultando venda…
          </p>
        ) : sale.error ? (
          <RequestAlert message={getApiError(sale.error)} />
        ) : found ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-xs text-content-muted">Venda {found.number}</p>
                <strong className="text-xl tabular-nums">
                  {formatCurrency(found.netTotalCents, found.currency)}
                </strong>
              </div>
              <PaymentStatePill sale={found} />
            </div>
            <ul className="grid gap-1 text-sm">
              {found.items.map((item) => (
                <li key={item.id} className="flex justify-between gap-2">
                  <span className={item.cancellation ? 'text-content-muted line-through' : ''}>
                    {item.packageId ? item.lineDisplayName : item.referenceDisplayName}
                    {item.quantity > 1 && !item.packageId ? ` ×${String(item.quantity)}` : ''}
                  </span>
                  <span className="tabular-nums text-content-muted">
                    {item.cancellation === 'PACKAGE_SESSION'
                      ? 'virou sessão do pacote'
                      : item.cancellation === 'APPOINTMENT_CANCELED'
                        ? 'cancelado com o atendimento'
                        : formatCurrency(item.allocatedTotalCents, found.currency)}
                  </span>
                </li>
              ))}
            </ul>
            <PaymentSummary sale={found} openLabel="A receber" align="start" />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setPaymentsOpen(true)}>
                Pagamentos do atendimento
              </Button>
              {canCreate && found.status === 'CONFIRMED' ? (
                <Button onClick={onOpenSale}>Adicionar à venda {found.number}</Button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-content-muted">
              {prepaid
                ? 'O cliente vai levar mais alguma coisa? A venda abre com o cliente; a sessão já está paga pelo pacote e não entra nela.'
                : 'A venda abre com o cliente e o serviço do agendamento. Dá para adicionar produtos, outros serviços e pacotes, e registrar o sinal ou o restante.'}
            </p>
            {canCreate ? (
              <Button className="justify-self-start" onClick={onOpenSale}>
                Gerar venda do atendimento
              </Button>
            ) : null}
          </>
        )}
      </div>
      {paymentsOpen && found ? (
        <SalePaymentsDialog
          sale={found}
          canManage={canManagePayments}
          financeContracted={financeContracted}
          today={today}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['company', 'sales'] })}
          onClose={() => setPaymentsOpen(false)}
        />
      ) : null}
    </div>
  );
}
